import React, { useEffect, useRef, useState } from 'react';

import { useLoadBannerAd } from '@client/hooks';
import {
  BannerAdObj,
  featureManager,
  isAdnamiAd
} from '@schibsted-nmp/advertising-shared';

import { getState } from '../../../state/reducer';

type Props = {
  id: string;
  adObj: BannerAdObj;
  className: string;
};

// leaving it here until verified that it works
export const useIframeHeightForBanner = (
  initialHeight: number,
  ref: React.RefObject<HTMLDivElement>
) => {
  const [height, setHeight] = useState(initialHeight);

  useEffect(() => {
    // Check if MutationObserver is supported
    if (typeof MutationObserver === 'undefined') {
      console.warn('MutationObserver is not supported by your browser.');
      return () => {};
    }

    function setHeightFromIframe(iframe: HTMLIFrameElement) {
      if (iframe) {
        const iframeHeight = iframe.getAttribute('height');
        const newHeight = iframeHeight
          ? parseInt(iframeHeight, 10)
          : initialHeight;
        if (!Number.isNaN(newHeight)) {
          setHeight(newHeight);
        }
      }
    }

    const { initialState } = getState();

    let heightObserver: MutationObserver | null = null;
    let iframeObserver: MutationObserver | null = null;
    let iframe: HTMLIFrameElement | null = null;

    if (
      initialState?.unleash.features &&
      featureManager(
        initialState.unleash.features
      ).isIframeHeightOnBannersEnabled()
    ) {
      heightObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'height' &&
            iframe
          ) {
            setHeightFromIframe(iframe);
          }
        });
      });

      iframeObserver = new MutationObserver(() => {
        if (ref.current) {
          const [firstIframe] =
            ref.current.getElementsByTagName('iframe') || [];
          if (firstIframe) {
            iframe = firstIframe;
            // Set default height
            setHeightFromIframe(iframe);
            // Add null check before using observer
            if (heightObserver) {
              heightObserver.observe(iframe, {
                attributes: true,
                attributeFilter: ['height']
              });
            }
          }
        }
      });

      if (ref.current) {
        iframeObserver.observe(ref.current, {
          childList: true,
          subtree: true
        });
      }
    }
    return () => {
      if (heightObserver) {
        heightObserver.disconnect();
      }
      if (iframeObserver) {
        iframeObserver.disconnect();
      }
    };
  }, [ref.current]);
  return height;
};

export default function Banner(props: Props) {
  const { id, adObj, className } = props;
  const ref = useRef<HTMLDivElement>(null);

  const height = useIframeHeightForBanner(adObj ? adObj.height : 0, ref);
  // Ensure adObj.width is defined before using it, otherwise, set default values
  const bannerAdStyle = adObj ? { width: adObj.width, height } : {};

  useLoadBannerAd({ placementId: id, ref });

  return (
    <div
      style={bannerAdStyle}
      className={className}
      // Ensure adObj is defined before checking if it's an Adnami ad
      data-is-adnami={adObj ? isAdnamiAd(adObj) : false}
      ref={ref}
      id={id}
    />
  );
}
